import React, { useEffect, useRef, useState } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Link2, Share2 } from 'lucide-react';

import { showToast } from '../../utils/showToast';
import { confirmAction } from '../../utils/sweetAlertUtils';
import Drawer from '../common/Drawer';
import Loader from '../common/Loader';

// The drawer behind every "Share" a member has: their Courses Played list
// (ShareCoursesPlayedDrawer) and their profile (ShareProfileDrawer). Nothing is public until they
// press "Create share link"; after that anyone with the link sees the page, without an account.
//
// Stopping sharing kills the link at once. Sharing again makes a new link, so an old one never
// comes back to life: that is also how a member "renews" a link that went further than meant.
//
// Each caller passes its own query, mutations, link builder and wording. The behaviour (create,
// copy, the phone's share sheet, stop) is the same for all of them.

export interface ShareLinkDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // The share code, null while not shared, undefined while loading.
  shareId: string | null | undefined;
  isLoading: boolean;
  share: UseMutationResult<string | null, unknown, void>;
  stopSharing: UseMutationResult<void, unknown, void>;
  buildUrl: (shareId: string) => string;
  // Shown before a link exists: what the link is, then what a stranger sees.
  intro: React.ReactNode;
  whatTheySee: React.ReactNode;
  // Shown once a link exists, above it.
  sharedText: string;
  // The title the phone's share sheet gets.
  nativeShareTitle: string;
  // The toast once sharing is stopped.
  stoppedText: string;
  inputId: string;
}

const ShareLinkDrawer: React.FC<ShareLinkDrawerProps> = ({
  isOpen,
  onClose,
  title,
  shareId,
  isLoading,
  share,
  stopSharing,
  buildUrl,
  intro,
  whatTheySee,
  sharedText,
  nativeShareTitle,
  stoppedText,
  inputId,
}) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const url = shareId != null ? buildUrl(shareId) : null;
  const canNativeShare =
    typeof navigator !== 'undefined' && navigator.share != null;

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = setTimeout(() => setCopied(false), 2000);
    return (): void => clearTimeout(timeout);
  }, [copied]);

  const handleCreate = (): void => {
    share.mutate(undefined, {
      onError: (error: unknown) => {
        showToast('Could not create your share link.', 'error');
        console.error('Could not create the share link:', error);
      },
    });
  };

  const handleCopy = async (): Promise<void> => {
    if (url == null) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard access (an insecure origin, or the browser refused). Select the link so the
      // member can copy it by hand rather than being told only that it failed.
      inputRef.current?.select();
      showToast('Press Ctrl+C (or Cmd+C) to copy the selected link.', 'info');
    }
  };

  const handleNativeShare = async (): Promise<void> => {
    if (url == null) {
      return;
    }

    try {
      await navigator.share({ title: nativeShareTitle, url });
    } catch {
      // Dismissing the share sheet rejects too. That is a normal outcome, not a failure.
    }
  };

  const handleStop = async (): Promise<void> => {
    const proceed = await confirmAction({
      title: 'Stop sharing?',
      text: 'The link stops working straight away, for everyone you sent it to. Sharing again later makes a new link.',
      icon: 'warning',
      confirmButtonText: 'Stop sharing',
      cancelButtonText: 'Keep sharing',
    });

    if (!proceed) {
      return;
    }

    stopSharing.mutate(undefined, {
      onSuccess: () => showToast(stoppedText, 'success'),
      onError: (error: unknown) => {
        showToast('Could not stop sharing.', 'error');
        console.error('Could not stop sharing:', error);
      },
    });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} size="small">
      <div className="flex flex-col gap-4 text-sm text-ink dark:text-white/80">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        )}

        {!isLoading && url == null && (
          <>
            <p>{intro}</p>
            <p className="text-ink-muted dark:text-white/60">{whatTheySee}</p>
            <button
              type="button"
              onClick={handleCreate}
              disabled={share.isPending}
              className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-semibold text-secondary transition hover:bg-opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
            >
              {share.isPending ? (
                <Loader size="sm" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              Create share link
            </button>
          </>
        )}

        {!isLoading && url != null && (
          <>
            <p>{sharedText}</p>

            <label htmlFor={inputId} className="sr-only">
              Your share link
            </label>
            <input
              id={inputId}
              ref={inputRef}
              type="text"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="h-11 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-ink outline-none focus:border-primary focus:ring-1 focus:ring-primary dark:border-white/20 dark:bg-white/5 dark:text-white"
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleCopy()}
                className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-secondary transition hover:bg-opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                {copied ? 'Copied' : 'Copy link'}
              </button>

              {canNativeShare && (
                <button
                  type="button"
                  onClick={() => void handleNativeShare()}
                  className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border border-secondary bg-white px-4 text-sm font-semibold text-ink transition hover:bg-secondary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/30 dark:bg-transparent dark:text-white"
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              )}
            </div>

            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-deep underline-offset-2 hover:underline dark:text-primary"
            >
              <ExternalLink className="h-4 w-4" />
              See what they see
            </a>

            <div className="mt-2 border-t border-gray-200 pt-4 dark:border-white/10">
              <button
                type="button"
                onClick={() => void handleStop()}
                disabled={stopSharing.isPending}
                className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-red-600 hover:underline disabled:cursor-wait disabled:opacity-60"
              >
                {stopSharing.isPending && <Loader size="sm" />}
                Stop sharing
              </button>
              <p className="mt-1 text-xs text-ink-muted dark:text-white/50">
                The link stops working straight away. Sharing again makes a new
                link.
              </p>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ShareLinkDrawer;
